/// <reference path="./prestige-connector-api.js" />
// @ts-check

const STATUS_CLASSES = {
  rejected: 'text-white/[.54] line-through',
  pending: 'text-[#ffbf00]',
  positive: 'text-[#4fa55f]',
  negative: 'text-[#ff8484]',
};

class PrestigeActivityFeed extends PrestigeBaseElement {
  constructor() {
    super();
    this.handleLoadMoreBtn = this.handleLoadMoreBtn.bind(this);
    this.limit = parseInt(this.dataset.limit || '6', 10);
    this.itemClass = this.dataset.itemClass || '';
    this.loadMoreLabel = this.dataset.loadmoreText;
    this.claimedLabel = this.dataset.claimedText;
    this.approvedLabel = this.dataset.approvedText;
    this.pendingLabel = this.dataset.pendingText;
    this.declinedLabel = this.dataset.declinedText;
    this.errorLabel = this.dataset.errorText;
    this.noActivitiesLabel = this.dataset.noActivitiesText;
  }

  dependencies() {
    return [this.store.pointsHistory];
  }

  onConnected() {
    if (this.loadMoreLabel) {
      this.addEventListener('click', this.handleLoadMoreBtn);
    }
  }

  onDisconnected() {
    if (this.loadMoreLabel) {
      this.removeEventListener('click', this.handleLoadMoreBtn);
    }
  }

  onVisible() {
    this.store.pointsHistory.load({ order: 'desc', limit: this.limit });
  }

  handleLoadMoreBtn(event) {
    const trigger = event.target.closest('[data-action="load-more"]');
    if (!trigger) return;
    event.preventDefault();

    const { data, isLoading } = this.store.pointsHistory;
    if (isLoading || !data || !data.next) return;

    this.store.pointsHistory.loadNext();
  }

  /**
   * Format timestamp as relative time (e.g., "2 hours ago")
   * @param {string} timestamp
   */
  timeAgo(timestamp) {
    if (typeof dayjs === 'undefined') return '';
    return dayjs(timestamp).fromNow();
  }

  /**
   * Get the status label for an activity
   * @param {import('./prestige-types').PrestigePointsHistoryEntry} activity
   */
  getStatusLabel(activity) {
    const statusLabels = {
      pending: this.pendingLabel,
      rejected: this.declinedLabel,
      negative: this.claimedLabel,
      positive: this.approvedLabel,
    };
    return statusLabels[activity.status] || this.approvedLabel;
  }

  /**
   * Get styling classes for an activity based on its status
   * @param {import('./prestige-types').PrestigePointsHistoryEntry} activity
   */
  getStatusClasses(activity) {
    return STATUS_CLASSES[activity.status] || STATUS_CLASSES.positive;
  }

  /**
   * Get the display label for an activity
   * For challenges, use full_challenge_name from properties if available
   * @param {import('./prestige-types').PrestigePointsHistoryEntry} activity
   */
  getDisplayLabel(activity) {
    // Reward redemptions show the reward title (connector maps Antavo coupon_change to action=reward_claim with a reward_name property).
    if (activity.action === 'reward_claim' && activity.properties) {
      const rewardName = activity.properties.find((p) => p.name === 'cf_reward_name')?.value;
      const rewardAmount = activity.properties.find((p) => p.name === 'cf_reward_amount')?.value;
      const converted = replaceRewardAmount(rewardName, rewardAmount);
      if (converted) {
        return converted;
      }

      const rewardTitle = activity.properties.find((p) => p.name === 'reward_name');
      if (rewardTitle?.value) {
        return rewardTitle.value;
      }
    }

    if (activity.label === 'Challenge' && activity.properties) {
      const challengeName = activity.properties.find((p) => p.name === 'full_challenge_name');
      if (challengeName?.value) {
        return challengeName.value;
      }
    }

    if (activity.action === 'campaign_bonus') {
      const label = activity.properties.find((p) => p.name === 'description');
      return label?.value || activity.label;
    }

    return activity.label;
  }

  /**
   * Get the status icon SVG for an activity
   * @param {import('./prestige-types').PrestigePointsHistoryEntry} activity
   */
  getStatusIcon(activity) {
    if (activity.status === 'rejected') {
      return /*html*/ `<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="12" height="12" rx="6" stroke="white" stroke-opacity="0.3"/>
        <path d="M5.93491 5.33223V3.74023H7.07091V5.33223L6.76691 7.50023H6.23891L5.93491 5.33223ZM5.87891 8.10023H7.11891V9.26023H5.87891V8.10023Z" fill="white"/>
      </svg>`;
    }
    if (activity.status === 'pending') {
      return /*html*/ `<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.50049 3L6.5 7.00405L9 9" stroke="white"/>
        <path d="M12.5 6.5C12.5 5.71207 12.3448 4.93185 12.0433 4.2039C11.7417 3.47595 11.2998 2.81451 10.7426 2.25736C10.1855 1.70021 9.52405 1.25825 8.7961 0.956723C8.06815 0.655195 7.28793 0.5 6.5 0.5" stroke="white" stroke-opacity="0.3" stroke-dasharray="2 2"/>
        <path d="M6.5 0.5C5.31331 0.5 4.15327 0.851894 3.16658 1.51118C2.17988 2.17047 1.41085 3.10754 0.956723 4.2039C0.502597 5.30026 0.383777 6.50666 0.615288 7.67054C0.8468 8.83443 1.41824 9.90353 2.25736 10.7426C3.09647 11.5818 4.16557 12.1532 5.32946 12.3847C6.49334 12.6162 7.69974 12.4974 8.7961 12.0433C9.89246 11.5892 10.8295 10.8201 11.4888 9.83342C12.1481 8.84673 12.5 7.68669 12.5 6.5" stroke="white" stroke-opacity="0.3"/>
      </svg>`;
    }
    // Default checkmark icon for positive/negative
    return /*html*/ `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="12" height="12" rx="6" stroke="white" stroke-opacity="0.3"/>
      <path d="M9.5 5.00098L6.5 8.99902L4.5 6.99902" stroke="white"/>
    </svg>`;
  }

  render() {
    const { data, isLoading, errors } = this.store.pointsHistory;
    if (!data || errors.length) {
      console.error('Failed to load prestige-activity-feed', { errors });
      return /*html*/ `<p class="text-xs">${this.errorLabel}</p>`;
    }

    if (data.items.length === 0) {
      return /*html*/ `<p class="text-xs">${this.noActivitiesLabel}</p>`;
    }

    const canLoadMore = !!data.next;

    return this.html`
        ${data.items.map((activity) => {
          const isPointGain = activity.points > 0;
          const statusClasses = this.getStatusClasses(activity);
          const isRejected = activity.status === 'rejected';

          return /*html*/ `
          <div class="flex justify-between border border-[rgba(255,255,255,0.3)] px-3 py-2 rounded-sm ${this.itemClass}">
            <div class="flex flex-col gap-1">
              <p class="text-xs ${isRejected ? statusClasses : ''}">${this.getDisplayLabel(activity)}</p>
              <p class="flex gap-2 text-xs items-center text-[rgba(255,255,255,0.54)]">
                ${this.getStatusIcon(activity)}
                ${this.getStatusLabel(activity)}
              </p>
            </div>
            <div class="flex flex-col gap-1">
              <p class="text-xs text-right ${statusClasses}">${isPointGain ? '+' : ''}${activity.points} pts</p>
              <p class="text-xs text-[rgba(255,255,255,0.54)] text-right">${this.timeAgo(activity.timestamp)}</p>
            </div>
          </div>
          `;
        })}
      ${
        canLoadMore && this.loadMoreLabel
          ? /*html*/ `
          <button
            type="button"
            data-action="load-more"
            ${isLoading ? 'disabled aria-disabled="true"' : ''}
            class="
              h-[40px]
              px-6 py-3
              text-xs leading-[1.3] text-center
              whitespace-nowrap select-none cursor-pointer
              border border-solid border-white border-opacity-30
              rounded-sm
              text-white bg-transparent backdrop-blur-xl
              transition-colors duration-300
              hover:bg-white hover:text-black
              disabled:opacity-75
              "
            >
            ${this.loadMoreLabel}
          </button>`
          : ``
      }
    `;
  }
}
customElements.define('prestige-activity-feed', PrestigeActivityFeed);

class PrestigeAccountWelcomePoints extends PrestigeBaseElement {
  constructor() {
    super();
    this.untilLevelText = this.dataset.untilLevelText;
    this.pointsText = this.dataset.pointsText;
  }

  dependencies() {
    return [this.store.profile];
  }

  onConnected() {
    this.store.profile.load();
  }

  render() {
    const { data, errors } = this.store.profile;

    if (!data || errors?.length > 0) {
      return this._initialContent || '';
    }

    const currentTier = data.tiers?.[0];
    if (!currentTier) {
      return this._initialContent || '';
    }

    const currentPoints = currentTier.points || 0;
    const nextTier = currentTier.next;
    const hasNextTier = !!nextTier?.points;

    const nextLevelPoints = hasNextTier
      ? currentPoints + nextTier.points
      : currentTier.threshold;

    const progressPercentage = nextLevelPoints > 0
      ? Math.min(Math.max((currentPoints / nextLevelPoints) * 100, 0), 100)
      : 0;

    const pointsLabel = hasNextTier ? this.untilLevelText : this.pointsText;

    const progressBar = /** @type {HTMLElement|null} */ (this.querySelector('[data-bind="progress-bar"]'));
    if (progressBar) progressBar.style.width = `${progressPercentage}%`;

    return this.mount({
      'current-points': currentPoints,
      'next-level-points': nextLevelPoints,
      'points-label': pointsLabel,
    });
  }
}
customElements.define('prestige-account-welcome-points', PrestigeAccountWelcomePoints);
